import React, { useCallback, useEffect, useRef, useState } from "react";
import { Form } from "react-bootstrap";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Loading, EmptyState, ErrorState } from "../common/WarrantyStates";
import { listCatalogueComponents, saveCatalogueComponent } from "../common/warrantyApi";
import { requestIdFor, settleRequestId } from "../common/clientRequest";
import ImagePreview from "./ImagePreview";
import {
  LABEL_MAX,
  URL_MAX,
  componentChanged,
  componentDraft,
  componentErrors,
  componentPayload,
  updatedText,
} from "./catalogueFields";
import "../warranty.css";

const FieldMsg = ({ id, text }) =>
  text ? (
    <span id={id} className="warranty-field-msg is-error" role="alert">
      {text}
    </span>
  ) : null;

// One editable row per component (always seven). Each row saves on its own.
const ComponentIcons = ({ country, accessToken }) => {
  const [view, setView] = useState({ status: "loading", items: [], message: "" });
  const [drafts, setDrafts] = useState({});
  const [rowState, setRowState] = useState({});
  const seq = useRef(0);
  const mounted = useRef(true);
  // One request id holder per row: a retry of the same body after an unclear answer reuses its id.
  const requestIds = useRef({});

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    const mine = ++seq.current;
    setView({ status: "loading", items: [], message: "" });
    const result = await listCatalogueComponents(accessToken, country);
    if (!mounted.current || mine !== seq.current) {
      return;
    }
    if (result.ok) {
      const items = Array.isArray(result.data?.items) ? result.data.items : [];
      const nextDrafts = {};
      items.forEach((item) => {
        nextDrafts[item.component] = componentDraft(item);
      });
      setDrafts(nextDrafts);
      setRowState({});
      setView({ status: "ready", items, message: "" });
    } else {
      setView({ status: "error", items: [], message: result.message });
    }
  }, [accessToken, country]);

  useEffect(() => {
    load();
  }, [load]);

  const setField = (component, field, value) => {
    setDrafts((prev) => ({ ...prev, [component]: { ...prev[component], [field]: value } }));
    setRowState((prev) => {
      const row = prev[component];
      if (!row?.errors?.[field] && !row?.message) {
        return prev;
      }
      const errors = { ...(row.errors || {}) };
      delete errors[field];
      return { ...prev, [component]: { ...row, errors, message: "" } };
    });
  };

  const save = async (item) => {
    const draft = drafts[item.component];
    const errors = componentErrors(draft);
    if (Object.keys(errors).length > 0) {
      setRowState((prev) => ({ ...prev, [item.component]: { errors, message: "" } }));
      return;
    }
    setRowState((prev) => ({ ...prev, [item.component]: { saving: true, errors: {}, message: "" } }));
    const body = componentPayload(country, item.component, draft);
    if (!requestIds.current[item.component]) {
      requestIds.current[item.component] = { current: null };
    }
    const idRef = requestIds.current[item.component];
    const result = await saveCatalogueComponent(accessToken, body, requestIdFor(idRef, JSON.stringify(body)));
    settleRequestId(idRef, result);
    if (!mounted.current) {
      return;
    }
    if (result.ok && result.data && result.data.component === item.component) {
      setView((prev) => ({
        ...prev,
        items: prev.items.map((row) => (row.component === item.component ? result.data : row)),
      }));
      setDrafts((prev) => ({ ...prev, [item.component]: componentDraft(result.data) }));
      setRowState((prev) => ({ ...prev, [item.component]: { errors: {}, message: "" } }));
      toast.success(`${result.data.label || item.component} saved.`);
    } else if (result.ok) {
      // Saved, but the answer is not the row: read the list again rather than guess.
      toast.success(`${draft.label.trim()} saved.`);
      load();
    } else {
      setRowState((prev) => ({ ...prev, [item.component]: { errors: {}, message: result.message } }));
    }
  };

  if (view.status === "loading") {
    return <Loading label="Loading the component icons" />;
  }
  if (view.status === "error") {
    return <ErrorState message={view.message} onRetry={load} />;
  }
  if (view.items.length === 0) {
    return <EmptyState title="No components for this market" />;
  }

  return (
    <div className="tableView warranty-ops-table formloginset warranty-catalogue-table">
      <table className="table">
        <thead>
          <tr>
            <th>Icon</th>
            <th>Key</th>
            <th>Label</th>
            <th>Icon URL</th>
            <th>Sort</th>
            <th>Active</th>
            <th>Updated</th>
            <th className="warranty-actions-cell">
              <span className="warranty-sr-only">Save</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {view.items.map((item) => {
            const key = item.component;
            const draft = drafts[key] || componentDraft(item);
            const state = rowState[key] || {};
            const errors = state.errors || {};
            const changed = componentChanged(item, draft);
            return (
              <tr key={key}>
                <td>
                  <ImagePreview src={draft.iconUrl.trim()} size={40} alt={`${draft.label || key} icon`} />
                </td>
                <td>
                  <code>{key}</code>
                </td>
                <td className="warranty-catalogue-label">
                  <Form.Control
                    type="text"
                    aria-label={`Label for ${key}`}
                    value={draft.label}
                    maxLength={LABEL_MAX}
                    isInvalid={Boolean(errors.label)}
                    aria-describedby={errors.label ? `warranty-cat-${key}-label-msg` : undefined}
                    onChange={(event) => setField(key, "label", event.target.value)}
                    disabled={state.saving}
                  />
                  <FieldMsg id={`warranty-cat-${key}-label-msg`} text={errors.label} />
                </td>
                <td className="warranty-catalogue-url">
                  <Form.Control
                    type="url"
                    aria-label={`Icon URL for ${key}`}
                    value={draft.iconUrl}
                    maxLength={URL_MAX}
                    placeholder="https://cdn.emotorad.com/... (blank for none)"
                    isInvalid={Boolean(errors.iconUrl)}
                    aria-describedby={errors.iconUrl ? `warranty-cat-${key}-url-msg` : undefined}
                    onChange={(event) => setField(key, "iconUrl", event.target.value)}
                    disabled={state.saving}
                    spellCheck={false}
                    autoComplete="off"
                  />
                  <FieldMsg id={`warranty-cat-${key}-url-msg`} text={errors.iconUrl} />
                </td>
                <td className="warranty-catalogue-sort">
                  <Form.Control
                    type="text"
                    inputMode="numeric"
                    aria-label={`Sort order for ${key}`}
                    value={draft.sortOrder}
                    maxLength={4}
                    isInvalid={Boolean(errors.sortOrder)}
                    aria-describedby={errors.sortOrder ? `warranty-cat-${key}-sort-msg` : undefined}
                    onChange={(event) => setField(key, "sortOrder", event.target.value)}
                    disabled={state.saving}
                  />
                  <FieldMsg id={`warranty-cat-${key}-sort-msg`} text={errors.sortOrder} />
                </td>
                <td>
                  <Form.Check
                    type="switch"
                    id={`warranty-cat-${key}-active`}
                    aria-label={`${key} active`}
                    checked={draft.active}
                    onChange={(event) => setField(key, "active", event.target.checked)}
                    disabled={state.saving}
                  />
                </td>
                <td className="warranty-nowrap">{updatedText(item)}</td>
                <td className="warranty-actions-cell">
                  <button
                    type="button"
                    className="warranty-inline-btn is-primary"
                    onClick={() => save(item)}
                    disabled={state.saving || !changed}
                    aria-label={`Save ${key}`}
                  >
                    {state.saving ? "Saving" : "Save"}
                  </button>
                  {state.message && (
                    <div className="warranty-dialog-error" role="alert">
                      {state.message}
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default ComponentIcons;
